// src/components/admin/bespokeai/DailyTokenCounter.jsx
// Shows today's Bespoke AI token usage per module against the organisation's daily limit.
import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { getDailyTokenUsage, DAILY_TOKEN_LIMIT } from '../../../api/aiUsageApi';

const MODULE_LABELS = {
  community:      'Community AI',
  communications: 'Communications AI',
  events:         'Events AI',
  reports:        'Reports AI',
};

function fmt(n) {
  return (n || 0).toLocaleString();
}

const DailyTokenCounter = forwardRef(function DailyTokenCounter({ orgId, onLimitReached }, ref) {
  const [usage, setUsage] = useState({ byModule: {}, totalTokens: 0, limitExceeded: false });
  const [loading, setLoading] = useState(true);

  const apply = useCallback((data) => {
    setUsage(data);
    setLoading(false);
    if (onLimitReached) onLimitReached(!!data.limitExceeded || (data.totalTokens || 0) >= DAILY_TOKEN_LIMIT);
  }, [onLimitReached]);

  const refresh = useCallback(async () => {
    if (!orgId) return;
    apply(await getDailyTokenUsage());
  }, [orgId, apply]);

  const update = useCallback((data) => {
    if (data) apply(data);
  }, [apply]);

  useImperativeHandle(ref, () => ({ refresh, update }), [refresh, update]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) return null;

  const total = usage.totalTokens || 0;
  const exceeded = usage.limitExceeded || total >= DAILY_TOKEN_LIMIT;

  const containerStyle = {
    margin: '0 0 10px 0',
    padding: '6px 12px',
    background: exceeded ? '#fff5f5' : '#f8f8f8',
    border: `1px solid ${exceeded ? '#fca5a5' : '#e0e0e0'}`,
    borderRadius: 6,
    fontSize: 11,
    color: exceeded ? '#b91c1c' : '#666',
    lineHeight: 1.6,
  };

  const rowStyle = {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
    alignItems: 'center',
  };

  return (
    <div style={containerStyle}>
      {exceeded && (
        <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>
          Daily AI usage limit reached. AI is unavailable until 00:00:01.
        </div>
      )}
      <div style={rowStyle}>
        <span style={{ fontWeight: 500, color: exceeded ? '#b91c1c' : '#444' }}>
          AI Usage Today:
        </span>
        {Object.entries(MODULE_LABELS).map(([key, label]) => (
          <span key={key}>
            {label}: <strong>{fmt(usage.byModule?.[key]?.totalTokens)}</strong>
          </span>
        ))}
        <span style={{ borderLeft: '1px solid #ccc', paddingLeft: 12 }}>
          Total: <strong style={{ color: exceeded ? '#b91c1c' : total > 160000 ? '#d97706' : '#333' }}>
            {fmt(total)} / {fmt(DAILY_TOKEN_LIMIT)}
          </strong>
        </span>
      </div>
      <div style={{ fontSize: 9, color: '#aaa', marginTop: 3 }}>
        Maximum {fmt(DAILY_TOKEN_LIMIT)} tokens per day across all 4 AI modules. Limit resets at 00:00:01.
      </div>
    </div>
  );
});

export default DailyTokenCounter;
